import type { IMessageElement } from 'client-types/';

import { normalizeAppLanguage } from './language';

const isForIdMatch = (id: string | number | undefined, forId: string) => {
  if (!forId || !id) {
    return false;
  }

  return forId === id.toString();
};

const escapeRegExp = (string: string) => {
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#escaping
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/** Human label for citation ref tokens (e.g. iz_memorije → Iz memorije). */
export type CitationKind = 'memory' | 'document' | 'web';

const CITATION_KIND_BY_NAME: Record<string, CitationKind> = {
  'Iz memorije': 'memory',
  'Prethodni razgovori': 'memory',
  'Iz dokumenata': 'document',
  'Sa weba': 'web',
  'From memory': 'memory',
  'Previous chats': 'memory',
  'From docs': 'document',
  'From web': 'web',
  iz_memorije: 'memory',
  iz_dokumenta: 'document',
  iz_dokumenata: 'document',
  iz_weba: 'web',
  sa_weba: 'web'
};

export const getCitationKind = (name: string): CitationKind | undefined =>
  CITATION_KIND_BY_NAME[name];

export const formatCitationRefLabel = (
  name: string,
  language?: string | null
): string => {
  const kind = getCitationKind(name);
  if (kind) {
    const english = normalizeAppLanguage(language) === 'en-US';
    const labels: Record<CitationKind, [string, string]> = {
      memory: ['Prethodni razgovori', 'Previous chats'],
      document: ['Iz dokumenata', 'From docs'],
      web: ['Sa weba', 'From web']
    };
    return labels[kind][english ? 1 : 0];
  }

  // Only format FON citation refs — other element names (source_1, etc.) stay as-is.
  if (!/^(?:iz|sa)_[a-z]+$/.test(name)) {
    return name;
  }
  const spaced = name.replace(/_/g, ' ');
  if (!spaced) {
    return name;
  }
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export const CITATION_CHIP_LABELS = [
  'Iz memorije',
  'Prethodni razgovori',
  'Iz dokumenata',
  'Sa weba',
  'From memory',
  'Previous chats',
  'From docs',
  'From web'
] as const;

const LEGACY_CITATION_TOKENS = new Set([
  'iz_memorije',
  'iz_dokumenta',
  'iz_dokumenata',
  'iz_weba',
  'sa_weba'
]);

const INTERNAL_CITATION_MARKER =
  /\[\[(?:iz_(?:memorije|dokumenta|dokumenata|weba)|sa_weba)\]\]/gi;

const GITHUB_REPOSITORY_URL =
  /https?:\/\/(?:www\.)?github\.com\/[a-z0-9_.-]+\/[a-z0-9_.-]+(?:\/[a-z0-9_./-]*)?(?:[?#][^\s<]*)?/gi;

export interface GitHubRepositoryReference {
  displayContent: string;
  hasRepository: boolean;
  repositoryUrl: string | null;
}

export const appendGitHubRepositoryReference = (
  content: string,
  repositoryUrl?: string | null
): string =>
  [content.trim(), repositoryUrl?.trim()].filter(Boolean).join('\n');

/**
 * Keep repository URLs in the persisted message for backend analysis while
 * removing them from the student-facing transcript.
 */
export const extractGitHubRepositoryReference = (
  content?: string
): GitHubRepositoryReference => {
  GITHUB_REPOSITORY_URL.lastIndex = 0;
  const repositoryUrl = content?.match(GITHUB_REPOSITORY_URL)?.[0] ?? null;
  const hasRepository = Boolean(repositoryUrl);
  GITHUB_REPOSITORY_URL.lastIndex = 0;

  if (!content || !hasRepository) {
    return {
      displayContent: content || '',
      hasRepository: false,
      repositoryUrl: null
    };
  }

  const displayContent = content
    .replace(GITHUB_REPOSITORY_URL, '')
    .replace(/[ \t]+([,.;:!?])/g, '$1')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n[ \t]*\n(?:[ \t]*\n)+/g, '\n\n')
    .trim();

  return { displayContent, hasRepository: true, repositoryUrl };
};

/** Remove control markers from persisted replies created by older backends. */
const stripInternalCitationMarkers = (content: string): string =>
  content
    .replace(INTERNAL_CITATION_MARKER, '')
    .replace(/[ \t]+([,.;:!?])/g, '$1')
    .replace(/\n[ \t]*\n(?:[ \t]*\n)+/g, '\n\n')
    .trim();

/** Whether an element name is a FON source citation chip. */
export const isCitationName = (name: string): boolean =>
  name in CITATION_KIND_BY_NAME || LEGACY_CITATION_TOKENS.has(name);

const citationSortIndex = (name: string): number => {
  const kind = getCitationKind(name);
  return kind ? ['memory', 'document', 'web'].indexOf(kind) : 3;
};

/** Side-panel citation elements for a message (chips below assistant reply). */
export const getCitationSideElements = (
  elements: IMessageElement[],
  messageId: string
): IMessageElement[] =>
  elements
    .filter(
      (e) =>
        isForIdMatch(messageId, e?.forId) &&
        e.display === 'side' &&
        isCitationName(e.name)
    )
    .sort((a, b) => citationSortIndex(a.name) - citationSortIndex(b.name));

const stripVisibleSourceLines = (content: string): string =>
  content
    .split('\n')
    .filter((line) => {
      const normalized = line.trim().replace(/^[-*_\s]+/, '');
      return !/^(?:sources?|izvori?)\s*:/i.test(normalized);
    })
    .join('\n')
    .trim();

export const prepareContent = ({
  elements,
  content,
  id,
  language
}: {
  elements: IMessageElement[];
  content?: string;
  id: string;
  language?: string;
}) => {
  const scopedElements = elements.filter((e) => isForIdMatch(id, e?.forId));
  const elementNames = scopedElements.map((e) => escapeRegExp(e.name));

  // Sort by descending length to avoid matching substrings
  elementNames.sort((a, b) => b.length - a.length);

  const elementRegexp = elementNames.length
    ? new RegExp(`(${elementNames.join('|')})`, 'g')
    : undefined;

  let preparedContent = content ? stripInternalCitationMarkers(content) : '';
  const inlinedElements = scopedElements.filter((e) => e.display === 'inline');
  const refElements: IMessageElement[] = [];

  // Also clean previously persisted replies. New replies are sanitized by the
  // backend, but old threads can still contain a redundant visible Source line.
  if (scopedElements.some((e) => e.display === 'side' && isCitationName(e.name))) {
    preparedContent = stripVisibleSourceLines(preparedContent);
  }

  if (elementRegexp) {
    preparedContent = preparedContent.replaceAll(elementRegexp, (match) => {
      const element = scopedElements.find((e) => e.name === match);
      const foundElement = !!element;

      const inlined = element?.display === 'inline';
      if (!foundElement) {
        // Element reference does not exist, return plain text
        return match;
      } else if (inlined) {
        // If element is inlined, add it to the list and return plain text
        if (inlinedElements.indexOf(element) === -1) {
          inlinedElements.push(element);
        }
        return match;
      } else {
        // Citation elements always render in the dedicated row below the
        // answer. Their localized labels can also occur naturally in prose
        // (for example, "Sa weba sam pronašao..."); replacing that phrase
        // with an icon corrupts the sentence and puts a source icon first.
        if (isCitationName(element.name)) {
          return match;
        }
        // Element is a reference, add it to the list and return link
        refElements.push(element);
        // spaces break markdown links. The address in the link is not used anyway
        return `[${match}](${match.replaceAll(' ', '_')})`;
      }
    });
  }

  if (language && preparedContent) {
    const prefix = `\`\`\`${language}`;
    const suffix = '```';
    if (!preparedContent.startsWith('```')) {
      preparedContent = `${prefix}\n${preparedContent}\n${suffix}`;
    }
  }
  return {
    preparedContent,
    inlinedElements,
    refElements
  };
};
